import { useEffect, useRef, useState } from 'react';

import { describeDeviceError, type DeviceName } from './deviceErrors';

export type PermissionState = 'pending' | 'granted' | 'denied' | 'missing';

interface PreviewOptions {
  cameraId: string;
  micId: string;
  cameraEnabled: boolean;
  micEnabled: boolean;
}

/**
 * Локальный предпросмотр до входа в комнату — отдельный от LiveKit поток.
 * Так пользователь проверяет камеру и микрофон, не занимая место в комнате
 * и не создавая лишних подписок для остальных участников.
 */
export function useLocalPreview({ cameraId, micId, cameraEnabled, micEnabled }: PreviewOptions) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permission, setPermission] = useState<PermissionState>('pending');
  const [error, setError] = useState('');

  // Держим актуальный поток в ref, чтобы гарантированно остановить дорожки
  // при размонтировании даже если состояние уже изменилось.
  const currentRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Ничего не запрошено — гасим поток и не трогаем разрешения.
    if (!cameraEnabled && !micEnabled) {
      stopStream(currentRef.current);
      currentRef.current = null;
      setStream(null);
      return;
    }

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPermission('missing');
        setError('Браузер не поддерживает доступ к камере и микрофону');
        return;
      }

      // Камеру и микрофон запрашиваем по отдельности: в одном вызове отказ
      // или отсутствие одного устройства обрушил бы и второе — человек
      // с рабочей камерой, но без микрофона, остался бы вообще без превью.
      const [video, audio] = await Promise.all([
        cameraEnabled
          ? capture({ video: cameraId ? { deviceId: { exact: cameraId } } : true })
          : Promise.resolve<CaptureResult>({ stream: null, failure: null }),
        micEnabled
          ? capture({ audio: micId ? { deviceId: { exact: micId } } : true })
          : Promise.resolve<CaptureResult>({ stream: null, failure: null }),
      ]);

      if (cancelled) {
        stopStream(video.stream);
        stopStream(audio.stream);
        return;
      }

      const tracks = [
        ...(video.stream?.getTracks() ?? []),
        ...(audio.stream?.getTracks() ?? []),
      ];

      stopStream(currentRef.current);

      if (tracks.length === 0) {
        currentRef.current = null;
        setStream(null);
        setPermission(video.failure?.kind ?? audio.failure?.kind ?? 'denied');
        setError(describeFailures(video.failure, audio.failure));
        return;
      }

      const next = new MediaStream(tracks);
      currentRef.current = next;
      setStream(next);
      setPermission('granted');
      // Одно устройство могло не подняться — сообщаем, но превью показываем.
      setError(describeFailures(video.failure, audio.failure));
    })();

    return () => {
      cancelled = true;
    };
  }, [cameraId, micId, cameraEnabled, micEnabled]);

  // Освобождаем камеру при уходе с экрана — иначе останется гореть индикатор.
  useEffect(
    () => () => {
      stopStream(currentRef.current);
      currentRef.current = null;
    },
    [],
  );

  return { stream, permission, error };
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

interface CaptureFailure {
  kind: PermissionState;
  /** Родительный падеж названия устройства: «камере», «микрофону». */
  device: DeviceName;
  message: string;
}

/**
 * Собирает одно сообщение по отказам обоих устройств.
 * Если причина одна и та же — не повторяем её дважды, а перечисляем устройства.
 */
function describeFailures(video: CaptureFailure | null, audio: CaptureFailure | null): string {
  if (!video && !audio) return '';
  if (!video) return audio!.message;
  if (!audio) return video.message;

  if (video.kind === audio.kind) {
    const devices = `${video.device} и ${audio.device}`;
    return video.kind === 'denied'
      ? `Доступ к ${devices} запрещён. Разрешите его в настройках браузера.`
      : `Устройства не найдены: нет доступа к ${devices}.`;
  }

  return `${video.message} ${audio.message}`;
}

interface CaptureResult {
  stream: MediaStream | null;
  failure: CaptureFailure | null;
}

/** Запрашивает одно устройство и переводит отказ в понятную формулировку. */
async function capture(constraints: MediaStreamConstraints): Promise<CaptureResult> {
  try {
    return { stream: await navigator.mediaDevices.getUserMedia(constraints), failure: null };
  } catch (err) {
    const name = err instanceof DOMException ? err.name : '';
    const device: DeviceName = constraints.video ? 'камере' : 'микрофону';
    // Формулировка общая с той, что показывается во встрече, — см.
    // `deviceErrors.ts`. Здесь остаётся только вид отказа: от него
    // зависит, предлагать ли выбрать другое устройство.
    const message = describeDeviceError(err, device);

    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return { stream: null, failure: { kind: 'denied', device, message } };
    }

    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return { stream: null, failure: { kind: 'missing', device, message } };
    }

    return {
      stream: null,
      failure: {
        kind: 'denied',
        device,
        message,
      },
    };
  }
}
